import React, { useEffect, useState, useCallback } from 'react';
import './Dashboard.css';
import SchoolsTable from './components/SchoolsTable';
import SchoolDetail from './components/SchoolDetail';
import AllOrdersTable from './components/AllOrdersTable';
import EmailTemplatesTab from './components/EmailTemplatesTab';
import DistrictsTable from './components/DistrictsTable';
import TagReportsTab from './components/TagReportsTab';
import SerializationConsole from './components/SerializationConsole';
import DatabaseTab from './components/DatabaseTab';
import DistrictDetail from './components/DistrictDetail';
import GlobalSearch from './components/GlobalSearch';

// Which top-nav sections each role may see. Admin sees everything; support (Jamie/sales) gets the
// customer-facing sections; warehouse gets fulfillment + serialization. Server-side gating enforces
// this too — this just shapes the UI so people only see what they can use.
const TABS_BY_ROLE = {
  // Dev = full access. Sales (gfadmin): customer-facing sections. Warehouse (TheFortKiosk):
  // fulfillment + serialization + database. Orders + Tag reports are shared by both.
  admin:     ['schools', 'districts', 'orders', 'serialization', 'database', 'emails', 'tagReports'],
  support:   ['schools', 'districts', 'orders', 'emails', 'tagReports'],
  warehouse: ['serialization', 'database', 'orders', 'tagReports'],
};

function Dashboard({ role = 'admin', onLogout }) {
  const allowedTabs = TABS_BY_ROLE[role] || TABS_BY_ROLE.admin;
  const canSee = (tab) => allowedTabs.includes(tab);
  const [schools, setSchools] = useState([]);
  const [students, setStudents] = useState([]);
  const [bags, setBags] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  // Top-level view: the schools list vs the system-wide orders list.
  const [topView, setTopView] = useState(allowedTabs[0] || 'schools');
  const [allOrders, setAllOrders] = useState([]);
  const [allOrdersLoading, setAllOrdersLoading] = useState(false);
  const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [districtsLoaded, setDistrictsLoaded] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [loadingDistrict, setLoadingDistrict] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  // Set when a global-search hit sends you somewhere: which sub-tab to open on the school page,
  // and the term to seed the Orders / Database filters with so the row you picked is on screen.
  const [schoolTab, setSchoolTab] = useState(null);
  const [ordersQuery, setOrdersQuery] = useState('');
  const [databaseQuery, setDatabaseQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState(null);

  const [refreshingSchools, setRefreshingSchools] = useState(false);
  const fetchSchools = useCallback(async () => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/schools`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.status === 403) {
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSchools(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    // Only roles that can see the Schools tab (support/admin) may hit /admin/schools;
    // the warehouse role gets a 403 there, which fetchSchools treats as a logout — so it
    // would bounce a warehouse user straight back to the login screen. Skip the fetch for
    // roles without the Schools tab and just show their landing view.
    if (canSee('schools')) {
      fetchSchools();
    } else {
      setLoading(false);
    }
  }, [fetchSchools]);

  const handleRefreshSchools = async () => {
    setRefreshingSchools(true);
    await fetchSchools();
    setRefreshingSchools(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_username');
    localStorage.removeItem('admin_password');
    onLogout();
  };

  const fetchSchoolData = async (schoolId) => {
    setLoadingStudents(true);
    setError(null);

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      };

      // Fetch students, bags, users, and invites in parallel
      const [studentsResponse, bagsResponse, usersResponse, invitesResponse] = await Promise.all([
        fetch(`${apiEndpoint}/admin/schools/${schoolId}/students`, {
          method: 'GET',
          headers
        }),
        fetch(`${apiEndpoint}/admin/schools/${schoolId}/bags`, {
          method: 'GET',
          headers
        }),
        fetch(`${apiEndpoint}/admin/schools/${schoolId}/users`, {
          method: 'GET',
          headers
        }),
        fetch(`${apiEndpoint}/admin/school/${schoolId}/invites`, {
          method: 'GET',
          headers
        })
      ]);

      console.log('Students API Response:', studentsResponse);
      console.log('Bags API Response:', bagsResponse);
      console.log('Users API Response:', usersResponse);
      console.log('Invites API Response:', invitesResponse);

      // Check for 403 on any response
      if (studentsResponse.status === 403 || bagsResponse.status === 403 || usersResponse.status === 403 || invitesResponse.status === 403) {
        console.log('403 Forbidden - Removing credentials and logging out');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!studentsResponse.ok) {
        throw new Error(`HTTP error fetching students! status: ${studentsResponse.status}`);
      }

      if (!bagsResponse.ok) {
        throw new Error(`HTTP error fetching bags! status: ${bagsResponse.status}`);
      }

      if (!usersResponse.ok) {
        throw new Error(`HTTP error fetching users! status: ${usersResponse.status}`);
      }

      if (!invitesResponse.ok) {
        throw new Error(`HTTP error fetching invites! status: ${invitesResponse.status}`);
      }

      const studentsData = await studentsResponse.json();
      const bagsData = await bagsResponse.json();
      const usersData = await usersResponse.json();
      const invitesData = await invitesResponse.json();
      
      console.log('Students data:', studentsData);
      console.log('Bags data:', bagsData);
      console.log('Users data:', usersData);
      console.log('Invites data:', invitesData);
      
      setStudents(studentsData);
      setBags(bagsData.bags);
      setUsers(usersData);
      setInvites(invitesData);
      setLoadingStudents(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLoadingStudents(false);
    }
  };

  // Fetch only students and bags
  const fetchStudentsAndBags = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      };

      const [studentsResponse, bagsResponse] = await Promise.all([
        fetch(`${apiEndpoint}/admin/schools/${schoolId}/students`, {
          method: 'GET',
          headers
        }),
        fetch(`${apiEndpoint}/admin/schools/${schoolId}/bags`, {
          method: 'GET',
          headers
        })
      ]);

      if (studentsResponse.status === 403 || bagsResponse.status === 403) {
        console.log('403 Forbidden - Removing credentials and logging out');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!studentsResponse.ok || !bagsResponse.ok) {
        throw new Error('Failed to fetch students or bags');
      }

      const studentsData = await studentsResponse.json();
      const bagsData = await bagsResponse.json();
      
      setStudents(studentsData);
      setBags(bagsData.bags);
    } catch (err) {
      console.error('Error fetching students and bags:', err);
      setError(err.message);
    }
  };

  // Fetch only users
  const fetchUsers = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      };

      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/users`, {
        method: 'GET',
        headers
      });

      if (response.status === 403) {
        console.log('403 Forbidden - Removing credentials and logging out');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const usersData = await response.json();
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    }
  };

  // Fetch only invites
  const fetchInvites = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      };

      const response = await fetch(`${apiEndpoint}/admin/school/${schoolId}/invites`, {
        method: 'GET',
        headers
      });

      if (response.status === 403) {
        console.log('403 Forbidden - Removing credentials and logging out');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch invites');
      }

      const invitesData = await response.json();
      setInvites(invitesData);
    } catch (err) {
      console.error('Error fetching invites:', err);
      setError(err.message);
    }
  };

  // Fetch events (lazy loaded)
  const fetchEvents = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      };

      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/events`, {
        method: 'GET',
        headers
      });

      if (response.status === 403) {
        console.log('403 Forbidden - Removing credentials and logging out');
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const eventsData = await response.json();
      console.log('Events data:', eventsData);
      setEvents(eventsData);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    }
  };

  // Fetch a school's claimed orders (lazy loaded when the Orders tab opens; also the
  // refresh callback after saving order notes).
  const fetchOrders = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/orders`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        }
      });

      if (response.status === 403) {
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const ordersData = await response.json();
      setOrders(ordersData);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    }
  };

  // Fetch every order in the system (claimed + unclaimed) for the global Orders list.
  const fetchAllOrders = async () => {
    setAllOrdersLoading(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/orders`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.status === 403) {
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      const data = await response.json();
      setAllOrders(data);
      setAllOrdersLoaded(true);
    } catch (err) {
      console.error('Error fetching all orders:', err);
      setError(err.message);
    } finally {
      setAllOrdersLoading(false);
    }
  };

  const showOrdersView = () => {
    setTopView('orders');
    if (!allOrdersLoaded) fetchAllOrders();
  };

  // Fetch every district for the Districts list (lazy loaded when the tab opens).
  const fetchDistricts = async () => {
    setDistrictsLoading(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/districts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.status === 403) {
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDistricts(data);
      setDistrictsLoaded(true);
    } catch (err) {
      console.error('Error fetching districts:', err);
      setError(err.message);
    } finally {
      setDistrictsLoading(false);
    }
  };

  const showDistrictsView = () => {
    setTopView('districts');
    if (!districtsLoaded) fetchDistricts();
  };

  const fetchDistrictDetail = async (districtId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/districts/${districtId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.status === 403) {
        localStorage.removeItem('admin_username');
        localStorage.removeItem('admin_password');
        onLogout();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSelectedDistrict(data);
    } catch (err) {
      console.error('Error fetching district detail:', err);
      setError(err.message);
    }
  };

  const handleDistrictClick = async (district) => {
    setLoadingDistrict(true);
    setSelectedDistrict({ id: district.id, name: district.name }); // placeholder while loading
    await fetchDistrictDetail(district.id);
    setLoadingDistrict(false);
  };

  const handleBackToDistricts = () => {
    setSelectedDistrict(null);
    setError(null);
  };

  const handleSchoolClick = async (school) => {
    setSelectedSchool(school);
    await fetchSchoolData(school.id);
  };

  // Open a school from the global orders list (click-through on a claimed order).
  const handleOpenSchoolById = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    if (school) handleSchoolClick(school);
  };

  // Navigate to whatever the global search turned up. The hit arrives pointed at one section —
  // either the default the server ranked first, or the one that was picked from the row's "see in"
  // choice — so this only has to get us there, and, where the target is a row in a long list, seed
  // that list's filter so it's the one you're looking at.
  const handleSearchNavigate = async (hit) => {
    setError(null);
    switch (hit.section) {
      case 'schools': {
        if (!hit.schoolId) return;
        // Land on the tab that actually shows the thing you searched for.
        const tab = hit.kind === 'product' ? 'bags' : hit.kind === 'order' ? 'orders' : 'students';
        setSchoolTab({ schoolId: hit.schoolId, tab });
        setSelectedDistrict(null);
        // A school created since this list was last loaded won't be in `schools` yet — refetch
        // once before giving up, so a brand-new account is still reachable from search.
        let school = schools.find(s => s.id === hit.schoolId);
        if (!school) {
          await fetchSchools();
          school = (await fetchSchoolById(hit.schoolId)) || null;
        }
        if (school) handleSchoolClick(school);
        break;
      }
      case 'districts': {
        if (!hit.districtId) return;
        setSelectedSchool(null);
        setTopView('districts');
        if (!districtsLoaded) fetchDistricts();
        // hit.title is the thing you searched for (a serial, a person), not the district's name —
        // only a district hit is named after its district, so prefer the name the server sent.
        handleDistrictClick({ id: hit.districtId, name: hit.districtName || hit.title });
        break;
      }
      case 'orders': {
        setSelectedSchool(null);
        setSelectedDistrict(null);
        setOrdersQuery(hit.orderNumber || hit.title);
        showOrdersView();
        break;
      }
      case 'database': {
        setSelectedSchool(null);
        setSelectedDistrict(null);
        setDatabaseQuery(hit.serialNumber || hit.title);
        setTopView('database');
        break;
      }
      default:
        break;
    }
  };

  // Re-read the schools list straight from the API — fetchSchools only updates state, and a
  // search hit needs the record in hand right now to open its detail page.
  const fetchSchoolById = async (schoolId) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/schools`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.find(s => s.id === schoolId) || null;
    } catch (err) {
      console.error('Error looking up school:', err);
      return null;
    }
  };

  const handleBackToSchools = () => {
    setSelectedSchool(null);
    setSchoolTab(null);
    setStudents([]);
    setBags([]);
    setUsers([]);
    setInvites([]);
    setEvents([]);
    setOrders([]);
    setError(null);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Generation Faraday Admin</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
        <div className="dashboard-content">
          <div className="loading">Loading schools...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Generation Faraday Admin</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
        <div className="dashboard-content">
          <div className="error">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Generation Faraday Admin</h1>
        <GlobalSearch onNavigate={handleSearchNavigate} />
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </div>
      {/* Bag-version reference, always visible on every tab. */}
      <div className="version-legend">
        <span className="version-legend-label">Bag versions</span>
        <span className="version-legend-item"><b>-V1</b> No RFID</span>
        <span className="version-legend-item"><b>-V2</b> RFID Card</span>
        <span className="version-legend-item"><b>-V3</b> Embedded NFC Tag</span>
        <span className="version-legend-item"><b>-V4</b> SuperCycle+ (Type V Tag)</span>
      </div>
      <div className="dashboard-content">
        {loadingStudents ? (
          <div className="loading">Loading school data...</div>
        ) : selectedSchool ? (
          <SchoolDetail 
            students={students}
            bags={bags}
            users={users}
            invites={invites}
            events={events}
            orders={orders}
            schoolId={selectedSchool.id}
            schoolName={selectedSchool.name}
            schoolDistrict={selectedSchool.district}
            initialTab={schoolTab && schoolTab.schoolId === selectedSchool.id ? schoolTab.tab : null}
            schoolNotes={selectedSchool.notes || ''}
            schoolInternal={selectedSchool.internal || false}
            schoolArchived={selectedSchool.archived || false}
            onRefreshSchools={fetchSchools}
            onBack={handleBackToSchools}
            onRefreshStudents={() => fetchStudentsAndBags(selectedSchool.id)}
            onRefreshUsers={() => fetchUsers(selectedSchool.id)}
            onRefreshInvites={() => fetchInvites(selectedSchool.id)}
            onLoadEvents={() => fetchEvents(selectedSchool.id)}
            onLoadOrders={() => fetchOrders(selectedSchool.id)}
            onSchoolDeleted={() => { handleBackToSchools(); fetchSchools(); }}
          />
        ) : selectedDistrict ? (
          <DistrictDetail
            district={selectedDistrict}
            onRefreshDistricts={fetchDistricts}
            loading={loadingDistrict}
            onBack={handleBackToDistricts}
            onOpenSchool={handleOpenSchoolById}
            onRefresh={() => fetchDistrictDetail(selectedDistrict.id)}
          />
        ) : (
          <>
            <div className="top-nav">
              {canSee('schools') && (
                <button
                  className={`top-nav-tab ${topView === 'schools' ? 'active' : ''}`}
                  onClick={() => setTopView('schools')}
                >
                  Schools ({schools.length})
                </button>
              )}
              {canSee('districts') && (
                <button
                  className={`top-nav-tab ${topView === 'districts' ? 'active' : ''}`}
                  onClick={showDistrictsView}
                >
                  Districts{districtsLoaded ? ` (${districts.length})` : ''}
                </button>
              )}
              {canSee('orders') && (
                <button
                  className={`top-nav-tab ${topView === 'orders' ? 'active' : ''}`}
                  onClick={showOrdersView}
                >
                  Orders{allOrdersLoaded ? ` (${allOrders.length})` : ''}
                </button>
              )}
              {canSee('serialization') && (
                <button
                  className={`top-nav-tab ${topView === 'serialization' ? 'active' : ''}`}
                  onClick={() => setTopView('serialization')}
                >
                  Serialization
                </button>
              )}
              {canSee('database') && (
                <button
                  className={`top-nav-tab ${topView === 'database' ? 'active' : ''}`}
                  onClick={() => setTopView('database')}
                >
                  Database
                </button>
              )}
              {canSee('emails') && (
                <button
                  className={`top-nav-tab ${topView === 'emails' ? 'active' : ''}`}
                  onClick={() => setTopView('emails')}
                >
                  Emails
                </button>
              )}
              {canSee('tagReports') && (
                <button
                  className={`top-nav-tab ${topView === 'tagReports' ? 'active' : ''}`}
                  onClick={() => setTopView('tagReports')}
                >
                  Tag reports
                </button>
              )}
              {topView === 'schools' && (
                <button
                  className="top-nav-refresh"
                  onClick={handleRefreshSchools}
                  disabled={refreshingSchools}
                  title="Reload the schools list (picks up newly created schools)"
                >
                  {refreshingSchools ? 'Refreshing…' : '↻ Refresh'}
                </button>
              )}
              {topView === 'districts' && (
                <button
                  className="top-nav-refresh"
                  onClick={fetchDistricts}
                  disabled={districtsLoading}
                  title="Reload the districts list"
                >
                  {districtsLoading ? 'Refreshing…' : '↻ Refresh'}
                </button>
              )}
            </div>
            {topView === 'schools' ? (
              <SchoolsTable
                schools={schools}
                onSchoolClick={handleSchoolClick}
              />
            ) : topView === 'districts' ? (
              <DistrictsTable
                districts={districts}
                loading={districtsLoading}
                onDistrictClick={handleDistrictClick}
              />
            ) : topView === 'emails' ? (
              <EmailTemplatesTab />
            ) : topView === 'tagReports' ? (
              <TagReportsTab />
            ) : topView === 'serialization' ? (
              <SerializationConsole role={role} onOpenSchool={handleOpenSchoolById} />
            ) : topView === 'database' ? (
              <DatabaseTab initialQuery={databaseQuery} />
            ) : (
              <AllOrdersTable
                orders={allOrders}
                loading={allOrdersLoading}
                initialQuery={ordersQuery}
                onOpenSchool={handleOpenSchoolById}
                onRefreshOrders={fetchAllOrders}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

